import { computed, effect, inject, Injectable, signal, untracked, WritableSignal } from '@angular/core';
import { ChatFilters, Highlight } from '../models/twitch.model';
import { AnalysisCacheData, DocumentEvent, TwitchChatStateService } from './twitch-chat-state.service.js';
import { TwitchChatService } from './twitch-chat.service.js';
import { FirebaseService } from './firebase.service.js';
import {
  Dir, GroupRun, HlKey, PatchDiff, ResultGroup, applyCacheDiff, applyHighlightDiff, applySetDelta, composePatch,
  diffCache, diffHighlights, filtersEqual, isEmptyPatch, keyAt, keyOfObject, pickSelection, purgeGroup,
  pushCapped, scalarEquals, setDelta,
} from '../utils/undo-history.js';

/**
 * Undo / redo of the user's work on the open VOD (Ctrl+Z / Ctrl+Y). See src/CLAUDE.md «Undo / redo».
 *
 * Two entry kinds:
 *  - patch: transact() diffs the document around a synchronous user action and undo applies the
 *    inverse onto the CURRENT state (utils/undo-history.ts) — automatic writers (live-poll, stream
 *    recalc, clip / marker refetch, AI results) keep what they changed meanwhile;
 *  - param: threshold / chat filters / Pre-Roll replay their canonical service path with the old
 *    or new value — peaks are derived from them and rebuilt from highlightStateCache + analysisCache.
 * AI and «Речь» runs are not steps (quota is spent); edits made on top of their results are.
 *
 * Per VOD, in memory: kept across VOD switches, dropped when the document is rebuilt from scratch
 * or a cloud copy replaces it (state.onDocumentEvent), cleared on account change and on reload.
 */

type ParamChange =
  | { kind: 'threshold'; before: number; after: number }
  | { kind: 'rewind'; before: number; after: number }
  | { kind: 'filters'; before: ChatFilters; after: ChatFilters };

interface EntryBase {
  id: number;
  label: string;
  at: number;
  coalesceKey?: string;
  selBefore: HlKey | null;
  selAfter: HlKey | null;
}

interface PatchEntry extends EntryBase {
  kind: 'patch';
  diff: PatchDiff;
  focusBefore: HlKey | null;
  focusAfter: HlKey | null;
  focusT: number | null;
  // Merge only: analysisCache entry of the merged startTime at merge time. Quick on the merged
  // moment overwrites it (same startTime as the first original) — undo puts it back so the
  // original doesn't inherit the merged description on the next rebuild.
  ai?: { t: number; before: AnalysisCacheData | undefined; swapped?: AnalysisCacheData };
}

interface ParamEntry extends EntryBase {
  kind: 'param';
  param: ParamChange;
}

type Entry = PatchEntry | ParamEntry;

interface VodHistory {
  undo: Entry[];
  redo: Entry[];
  lastRecordedId: number | null; // coalescing only into the step recorded last
}

export type HistoryResult =
  | { ok: true; label: string; entryId: number }
  | { ok: false; message: string };

export interface HistoryApplyOptions {
  /** Moments the current tab / region shows — selection never lands on a hidden one. */
  visible?: () => readonly Highlight[];
}

interface Capture {
  highlights: readonly Highlight[];
  cache: Map<number, unknown>;
  scalars: Record<string, unknown>;
  merge: ReadonlySet<number>;
  selected: HlKey | null;
}

const PARAM_LABEL: Record<ParamChange['kind'], (b: any, a: any) => string> = {
  threshold: (b, a) => `Чувствительность ${b} → ${a}`,
  rewind: (b, a) => `Pre-Roll ${b} → ${a} с`,
  filters: () => 'Фильтры чата',
};

@Injectable({ providedIn: 'root' })
export class UndoHistoryService {
  private state = inject(TwitchChatStateService);
  private chat = inject(TwitchChatService);
  private firebase = inject(FirebaseService);

  static readonly MAX_STEPS = 100;
  static readonly MAX_VODS = 20;
  static readonly COALESCE_MS = 1500;

  private stacks = new Map<string, VodHistory>(); // insertion order = LRU
  private rev = signal(0);                         // bumped on every stack mutation
  private docGen = signal(0);                      // key of VOD-less documents (file import without id)
  private nextId = 1;
  private depth = 0;
  private applying = false;
  private pendingFresh = false;
  private gesture: string | null = null;

  // Document fields a patch step can change besides highlights[] / highlightStateCache.
  // Threshold / filters / Pre-Roll are param steps; view state and global prefs are not steps.
  private readonly scalarSignals: Record<string, WritableSignal<any>> = {
    regionRange: this.state.regionRange,
    regionEnabled: this.state.regionEnabled,
    searchQuery: this.state.searchQuery,
    searchBinMatches: this.state.searchBinMatches,
    searchExactMatch: this.state.searchExactMatch,
    searchInputValue: this.state.searchInputValue,
    isAnalyzed: this.state.isAnalyzed,
  };

  readonly docKey = computed(() => {
    const v = this.state.videoId();
    return v != null && v !== '' ? String(v) : `doc#${this.docGen()}`;
  });

  private top(which: 'undo' | 'redo'): Entry | null {
    const list = this.stacks.get(this.docKey())?.[which];
    return list?.length ? list[list.length - 1] : null;
  }

  readonly canUndo = computed(() => { this.rev(); return !!this.top('undo'); });
  readonly canRedo = computed(() => { this.rev(); return !!this.top('redo'); });
  readonly undoLabel = computed(() => { this.rev(); return this.top('undo')?.label ?? null; });
  readonly redoLabel = computed(() => { this.rev(); return this.top('redo')?.label ?? null; });
  readonly undoTopId = computed(() => { this.rev(); return this.top('undo')?.id ?? null; });

  constructor() {
    this.state.onDocumentEvent = e => this.onDocumentEvent(e);

    // resetState can't know the next VOD's id — it arrives later (onMeta / file import /
    // library open), and that document is built from scratch: drop its old history.
    effect(() => {
      const v = this.state.videoId();
      untracked(() => {
        if (v == null || v === '' || !this.pendingFresh) return;
        this.pendingFresh = false;
        this.drop(String(v));
      });
    });

    // Another account (or logout → login) must never see the previous one's steps.
    let seenUid: string | null | undefined;
    effect(() => {
      const uid = this.firebase.currentUser()?.uid ?? null;
      untracked(() => {
        if (seenUid !== undefined && uid !== seenUid) this.clearAll();
        seenUid = uid;
      });
    });
  }

  // --- Recording ---

  /** Slider gesture: pointerdown…pointerup on a range coalesces into one step, pauses included. */
  beginGesture(key: string): void {
    this.gesture = key;
    // A new drag is a new step — never folded into the one a previous drag / key run recorded.
    const hist = this.stacks.get(untracked(() => this.docKey()));
    if (hist) hist.lastRecordedId = null;
  }
  endGesture(): void { this.gesture = null; }

  /**
   * Run a synchronous user action and record what it changed. Returns the entry id (null when
   * nothing changed or recording is suspended). `focus` = the moment acted on (an object from
   * state.highlights() before the action); `focusAfter` resolves it after (e.g. the merged moment).
   */
  transact(
    label: string | (() => string),
    fn: () => void,
    opts: {
      focus?: Highlight | null;
      focusAfter?: () => Highlight | null;
      coalesceKey?: string;
      aiKey?: () => number | null;
    } = {},
  ): number | null {
    if (this.applying || this.depth > 0) { fn(); return null; }
    const key = untracked(() => this.docKey());
    const b = this.capture();
    this.depth++;
    try { fn(); } finally { this.depth--; }
    if (untracked(() => this.docKey()) !== key) return null;
    const a = this.capture();

    const scalars: Record<string, [unknown, unknown]> = {};
    for (const name of Object.keys(this.scalarSignals)) {
      if (!scalarEquals(name, b.scalars[name], a.scalars[name])) scalars[name] = [b.scalars[name], a.scalars[name]];
    }
    const diff: PatchDiff = {
      hl: diffHighlights(b.highlights, a.highlights),
      cache: diffCache(b.cache, a.cache),
      scalars,
      merge: setDelta(b.merge, a.merge),
    };
    const focusBefore = keyOfObject(b.highlights, opts.focus ?? null);
    const afterObj = opts.focusAfter ? untracked(opts.focusAfter) : null;
    const focusAfter = opts.focusAfter ? keyOfObject(a.highlights, afterObj) : focusBefore;
    const focusT = (opts.focus ?? afterObj)?.startTime ?? null;
    const text = typeof label === 'function' ? untracked(label) : label;

    // The VOD's history is created only when a step is recorded — a no-op must not take an LRU slot.
    const hist = this.stacks.get(key);
    const now = performance.now();
    const top = hist?.undo[hist.undo.length - 1];
    if (hist && top && top.kind === 'patch' && this.canCoalesce(hist, top, opts.coalesceKey, now)) {
      this.touch(key);
      top.diff = composePatch(top.diff, diff);
      top.focusAfter = focusAfter;
      top.selAfter = a.selected;
      top.label = text;
      top.at = now;
      if (isEmptyPatch(top.diff)) { hist.undo.pop(); hist.lastRecordedId = null; }
      this.bump();
      return top.id;
    }
    if (isEmptyPatch(diff)) return null;

    let ai: PatchEntry['ai'];
    const aiT = opts.aiKey ? untracked(opts.aiKey) : null;
    if (aiT != null) ai = { t: aiT, before: untracked(() => this.state.analysisCache().get(aiT)) };

    const entry: PatchEntry = {
      kind: 'patch', id: this.nextId++, label: text, at: now, coalesceKey: opts.coalesceKey,
      diff, focusBefore, focusAfter, focusT, selBefore: b.selected, selAfter: a.selected, ai,
    };
    this.push(this.historyFor(key), entry);
    return entry.id;
  }

  /** Threshold / chat filters / Pre-Roll: run the service call and record old → new value. */
  transactParam(
    kind: ParamChange['kind'],
    fn: () => void,
    opts: { label?: (before: any, after: any) => string; coalesceKey?: string } = {},
  ): number | null {
    if (this.applying || this.depth > 0) { fn(); return null; }
    const key = untracked(() => this.docKey());
    const before = this.readParam(kind);
    const selBefore = this.selectedKey();
    this.depth++;
    try { fn(); } finally { this.depth--; }
    if (untracked(() => this.docKey()) !== key) return null;
    const after = this.readParam(kind);
    const selAfter = this.selectedKey();
    const labelOf = opts.label ?? PARAM_LABEL[kind];

    const hist = this.stacks.get(key);
    const now = performance.now();
    const top = hist?.undo[hist.undo.length - 1];
    if (hist && top && top.kind === 'param' && top.param.kind === kind && this.canCoalesce(hist, top, opts.coalesceKey, now)) {
      this.touch(key);
      top.param.after = after as never;
      top.selAfter = selAfter;
      top.label = labelOf(top.param.before, after);
      top.at = now;
      if (this.paramEqual(kind, top.param.before, after)) { hist.undo.pop(); hist.lastRecordedId = null; }
      this.bump();
      return top.id;
    }
    if (this.paramEqual(kind, before, after)) return null;
    const entry: ParamEntry = {
      kind: 'param', id: this.nextId++, label: labelOf(before, after), at: now, coalesceKey: opts.coalesceKey,
      param: { kind, before, after } as ParamChange, selBefore, selAfter,
    };
    this.push(this.historyFor(key), entry);
    return entry.id;
  }

  // --- Undo / redo ---

  undo(opts: HistoryApplyOptions = {}): HistoryResult | null { return this.step('undo', opts); }
  redo(opts: HistoryApplyOptions = {}): HistoryResult | null { return this.step('redo', opts); }

  private step(dir: Dir, opts: HistoryApplyOptions): HistoryResult | null {
    const key = this.docKey();
    const hist = this.stacks.get(key);
    const from = dir === 'undo' ? hist?.undo : hist?.redo;
    const e = from?.[from.length - 1];
    if (!hist || !from || !e) return null;
    // A filter replay re-filters the raw chat; without it the bins collapse (cloud copy, chat
    // not downloaded on this device).
    if (e.kind === 'param' && e.param.kind === 'filters' && this.state.needsChatRedownload()) {
      return { ok: false, message: `Нельзя ${dir === 'undo' ? 'отменить' : 'повторить'}: сначала скачайте чат` };
    }
    // A merge undone / redone under a running AI analysis: its results are matched by startTime
    // and would land on the wrong side (the merged span's description on the first original).
    if (e.kind === 'patch' && e.ai && (this.state.isAnalyzingQuick() || this.state.isAnalyzingDeep())) {
      return { ok: false, message: `Нельзя ${dir === 'undo' ? 'отменить' : 'повторить'} объединение, пока идёт ИИ-анализ` };
    }
    const s = this.state;
    const prevKey = this.selectedKey();

    this.applying = true;
    try {
      if (e.kind === 'patch') this.applyPatch(e, dir);
      else this.applyParam(e.param, dir);
    } finally {
      this.applying = false;
    }

    // Selection: the affected moment, without seek. Inserts / removals shift indices, and a
    // rebuild (param replay) can leave the index on another moment — always re-resolve.
    const list = s.highlights();
    const visible = opts.visible ? new Set(opts.visible()) : null;
    const isVisible = (h: Highlight) => !visible || visible.has(h);
    const undo = dir === 'undo';
    const candidates = e.kind === 'patch'
      ? [undo ? e.focusBefore : e.focusAfter, undo ? e.selBefore : e.selAfter, prevKey]
      : [undo ? e.selBefore : e.selAfter, prevKey];
    let idx = pickSelection(list, candidates, isVisible, e.kind === 'patch' ? e.focusT : null, s.currentHighlightIndex());
    // Nothing on this tab (e.g. undoing the search that filled «Поиск») — keep the previous
    // selection by key: inserts / removals shifted its index.
    if (idx < 0) idx = pickSelection(list, [prevKey, ...candidates], () => true, null, -1);
    if (idx >= 0) {
      if (idx !== s.currentHighlightIndex()) s.currentHighlightIndex.set(idx);
    } else if (s.currentHighlightIndex() >= list.length) {
      s.currentHighlightIndex.set(Math.max(0, list.length - 1));
    }

    from.pop();
    (undo ? hist.redo : hist.undo).push(e);
    hist.lastRecordedId = null;
    this.touch(key);
    this.bump();
    return { ok: true, label: e.label, entryId: e.id };
  }

  private applyPatch(e: PatchEntry, dir: Dir): void {
    const s = this.state;
    const cacheChanged = applyCacheDiff(s.highlightStateCache as Map<number, unknown>, e.diff.cache, dir);
    for (const [name, [b, a]] of Object.entries(e.diff.scalars)) {
      this.scalarSignals[name].set(dir === 'undo' ? b : a);
    }
    if (e.diff.merge) s.selectedToMerge.set(applySetDelta(s.selectedToMerge(), e.diff.merge, dir));

    const cur = s.highlights();
    let next = applyHighlightDiff(cur, e.diff.hl, dir);
    if (e.ai) next = this.syncMergeAi(e, dir, next);
    if (next !== cur) s.highlights.set(next as Highlight[]);
    // highlightStateCache isn't a signal — a cache-only step must still reach the autosave.
    else if (cacheChanged) s.highlights.set(cur.slice());
  }

  /**
   * Merge steps: description / sentiment / isAnalyzed of the moment at the merged startTime move
   * as one AI-owned group (the field diff alone can't — Quick may have run on either side since).
   * Undo puts back the first original's analysisCache entry from before the merge (Quick on the
   * merged moment overwrote it — same startTime) and keeps what Quick wrote on the merged span for
   * the redo; redo keeps what Quick wrote on the original meanwhile for the next undo.
   */
  private syncMergeAi(e: PatchEntry, dir: Dir, list: readonly Highlight[]): readonly Highlight[] {
    const ai = e.ai!;
    const s = this.state;
    const cache = s.analysisCache();
    const cur = cache.get(ai.t);
    const orig = e.diff.hl.changed.find(c => c.key === `peak|${ai.t}`) ?? null; // peak-first merge
    const merged = orig?.after ?? e.diff.hl.added.find(x => x.h.isMerged && x.h.startTime === ai.t)?.h ?? null;
    const patch = (isMerged: boolean, v: { description: string; sentiment: string; isAnalyzed: boolean }) => {
      const i = list.findIndex(h => h.source === 'peak' && !!h.isMerged === isMerged && h.startTime === ai.t);
      if (i === -1) return;
      const h = list[i];
      if (h.description === v.description && h.sentiment === v.sentiment && !!h.isAnalyzed === v.isAnalyzed) return;
      const out = list.slice();
      out[i] = { ...h, ...v };
      list = out;
    };
    if (dir === 'undo') {
      ai.swapped = cur !== ai.before ? cur : undefined;
      if (ai.swapped) {
        const next = new Map(cache);
        if (ai.before) next.set(ai.t, ai.before); else next.delete(ai.t);
        s.analysisCache.set(next);
      }
      if (orig) {
        patch(false, ai.before
          ? { description: ai.before.description, sentiment: ai.before.sentiment, isAnalyzed: true }
          : { description: orig.before.description, sentiment: orig.before.sentiment, isAnalyzed: !!orig.before.isAnalyzed });
      }
      return list;
    }
    ai.before = cur;
    const swapped = ai.swapped;
    ai.swapped = undefined;
    if (swapped) s.analysisCache.set(new Map(cache).set(ai.t, swapped));
    if (merged) {
      patch(true, swapped
        ? { description: swapped.description, sentiment: swapped.sentiment, isAnalyzed: true }
        : { description: merged.description, sentiment: merged.sentiment, isAnalyzed: !!merged.isAnalyzed });
    }
    return list;
  }

  private applyParam(p: ParamChange, dir: Dir): void {
    const undo = dir === 'undo';
    switch (p.kind) {
      case 'threshold': this.chat.updatePeakMessageThreshold(undo ? p.before : p.after); break;
      case 'rewind': this.chat.updateRewindOffset(undo ? p.before : p.after); break;
      case 'filters': this.chat.updateFilters(undo ? p.before : p.after); break;
    }
  }

  // --- Clearing ---

  private onDocumentEvent(e: DocumentEvent): void {
    switch (e.kind) {
      case 'reset':
        this.pendingFresh = true;
        this.docGen.update(n => n + 1);
        break;
      case 'loaded':
        this.pendingFresh = false;
        if (e.origin === 'cloud') this.drop(e.videoId);
        break;
      case 'replaced':
        this.drop(e.videoId);
        break;
      case 'results-replaced':
        this.purge(e.videoId, e.group, { scope: e.scope, before: e.before, after: e.after });
        break;
    }
  }

  /** A newer AI / «Речь» run replaced a result group — steps must not bring the old results back. */
  private purge(videoId: string, group: ResultGroup, run: GroupRun): void {
    const hist = this.stacks.get(videoId);
    if (!hist) return;
    const prune = (list: Entry[]) => list.filter(e => {
      if (e.kind !== 'patch') return true;
      e.diff = purgeGroup(e.diff, group, run);
      return !isEmptyPatch(e.diff);
    });
    hist.undo = prune(hist.undo);
    hist.redo = prune(hist.redo);
    hist.lastRecordedId = null;
    this.bump();
  }

  private drop(videoId: string): void {
    if (this.stacks.delete(videoId)) this.bump();
  }

  clearAll(): void {
    if (!this.stacks.size) return;
    this.stacks.clear();
    this.bump();
  }

  // --- Internals ---

  private capture(): Capture {
    return untracked(() => {
      const s = this.state;
      const highlights = s.highlights();
      const scalars: Record<string, unknown> = {};
      for (const [name, sig] of Object.entries(this.scalarSignals)) scalars[name] = sig();
      return {
        highlights,
        cache: new Map(s.highlightStateCache),
        scalars,
        merge: s.selectedToMerge(),
        selected: keyAt(highlights, s.currentHighlightIndex()),
      };
    });
  }

  private selectedKey(): HlKey | null {
    return untracked(() => keyAt(this.state.highlights(), this.state.currentHighlightIndex()));
  }

  private readParam(kind: ParamChange['kind']): any {
    return untracked(() => kind === 'threshold' ? this.state.peakMessageThreshold()
      : kind === 'rewind' ? this.state.rewindOffsetSeconds()
      : this.state.filterSettings());
  }

  private paramEqual(kind: ParamChange['kind'], a: any, b: any): boolean {
    return kind === 'filters' ? filtersEqual(a, b) : a === b;
  }

  private canCoalesce(hist: VodHistory, top: Entry, key: string | undefined, now: number): boolean {
    return !!key && top.coalesceKey === key && hist.lastRecordedId === top.id
      && (this.gesture === key || now - top.at <= UndoHistoryService.COALESCE_MS);
  }

  private historyFor(key: string): VodHistory {
    let hist = this.stacks.get(key);
    if (!hist) {
      hist = { undo: [], redo: [], lastRecordedId: null };
      this.stacks.set(key, hist);
    }
    this.touch(key);
    return hist;
  }

  private push(hist: VodHistory, entry: Entry): void {
    pushCapped(hist.undo, entry, UndoHistoryService.MAX_STEPS);
    hist.redo.length = 0;
    hist.lastRecordedId = entry.id;
    this.bump();
  }

  /** LRU: most recently used VOD last; evict the oldest beyond MAX_VODS. */
  private touch(key: string): void {
    const hist = this.stacks.get(key);
    if (!hist) return;
    this.stacks.delete(key);
    this.stacks.set(key, hist);
    while (this.stacks.size > UndoHistoryService.MAX_VODS) {
      const oldest = this.stacks.keys().next().value as string;
      this.stacks.delete(oldest);
    }
  }

  private bump(): void {
    this.rev.update(n => n + 1);
  }
}
